import { Injectable } from "@angular/core";

type SchemaField = {
  default?: unknown;
  const?: unknown;
  propertyDefault?: unknown;
  enum?: unknown[];
  type?: string;
};

@Injectable({ providedIn: "root" })
export class SchemaDefaultComparatorService {
  getSchemaDefault(schemaField: SchemaField | undefined | null): unknown {
    if (!schemaField) {
      return undefined;
    }
    if (schemaField.const !== undefined) {
      return schemaField.const;
    }
    if (schemaField.propertyDefault !== undefined) {
      return schemaField.propertyDefault;
    }
    return schemaField.default;
  }

  isValueAtSchemaDefault(
    currentValue: unknown,
    schemaField: SchemaField | undefined | null
  ): boolean {
    const schemaDefault = this.getSchemaDefault(schemaField);
    const normalizedCurrent = this.normalizeValue(currentValue, schemaField);
    const normalizedDefault = this.normalizeValue(schemaDefault, schemaField);

    return this.isEqual(normalizedCurrent, normalizedDefault);
  }

  isModelAtSchemaDefaults(
    model: Record<string, unknown>,
    schemaProperties: Record<string, SchemaField>,
    options?: {
      ignoreKeys?: string[];
      keyTransform?: (key: string) => string;
      compareOnlyModelKeys?: boolean;
    }
  ): boolean {
    if (!model || !schemaProperties) {
      return true;
    }

    const ignored = new Set(options?.ignoreKeys || []);
    const keysToCheck = options?.compareOnlyModelKeys
      ? Object.keys(model)
      : Object.keys(schemaProperties);

    for (const rawKey of keysToCheck) {
      if (ignored.has(rawKey)) {
        continue;
      }

      const schemaKey = options?.keyTransform ? options.keyTransform(rawKey) : rawKey;
      const schemaField = schemaProperties[schemaKey];
      if (!schemaField) {
        continue;
      }

      const currentValue = model[rawKey];
      if (!this.isValueAtSchemaDefault(currentValue, schemaField)) {
        return false;
      }
    }

    return true;
  }

  private normalizeValue(value: unknown, schemaField: SchemaField | undefined | null): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    const normalizedEnum = this.normalizeEnumValue(value, schemaField?.enum);
    const type = schemaField?.type;

    if (type === "boolean") {
      return this.normalizeBoolean(normalizedEnum);
    }

    if (type === "number" || type === "integer") {
      return this.normalizeNumber(normalizedEnum);
    }

    if (typeof normalizedEnum === "boolean") {
      return normalizedEnum;
    }

    if (typeof normalizedEnum === "number") {
      return normalizedEnum;
    }

    if (typeof normalizedEnum === "string") {
      const boolLike = this.normalizeBoolean(normalizedEnum);
      if (typeof boolLike === "boolean") {
        return boolLike;
      }
      const numberLike = this.normalizeNumber(normalizedEnum);
      if (typeof numberLike === "number") {
        return numberLike;
      }
    }

    return normalizedEnum;
  }

  private normalizeEnumValue(value: unknown, enumValues?: unknown[]): unknown {
    if (!enumValues || !Array.isArray(enumValues) || enumValues.length === 0) {
      return value;
    }

    const exactIndex = enumValues.findIndex((entry) => this.isEqual(entry, value));
    if (exactIndex > -1) {
      const next = enumValues[exactIndex + 1];
      if (typeof enumValues[exactIndex] === "number" && typeof next === "string") {
        return next;
      }
      return enumValues[exactIndex];
    }

    if (typeof value === "string") {
      const numeric = this.normalizeNumber(value);
      if (typeof numeric === "number") {
        const numericIndex = enumValues.findIndex((entry) => this.isEqual(entry, numeric));
        if (numericIndex > -1) {
          const next = enumValues[numericIndex + 1];
          if (typeof enumValues[numericIndex] === "number" && typeof next === "string") {
            return next;
          }
          return enumValues[numericIndex];
        }
      }
    }

    return value;
  }

  private normalizeBoolean(value: unknown): unknown {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "string") {
      const lower = value.trim().toLowerCase();
      if (lower === "true") {
        return true;
      }
      if (lower === "false") {
        return false;
      }
      if (lower === "1") {
        return true;
      }
      if (lower === "0") {
        return false;
      }
    }
    if (typeof value === "number") {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
    }
    return value;
  }

  private normalizeNumber(value: unknown): unknown {
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") {
        return value;
      }
      const parsed = Number(trimmed);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return value;
  }

  private isEqual(left: unknown, right: unknown): boolean {
    if (left === right) {
      return true;
    }

    if (typeof left === "number" && typeof right === "number") {
      return !Number.isNaN(left) && !Number.isNaN(right) && left === right;
    }

    return false;
  }
}
